import { Product, CryptoAsset } from '../types';
import { DB } from './db';

// CONFIGURATION
const CRYPTO_BOT_TOKEN = '522341:AAyZwA6fA5oxRHI2uzqJo1orLIADkHI7qPz'; 

// IMPORTANT: We now point to our internal route. 
// Vercel (prod) or Vite (dev) will handle the forwarding to pay.crypt.bot
const CREATE_INVOICE_ENDPOINT = '/api/payment/createInvoice';
const GET_INVOICES_ENDPOINT = '/api/payment/getInvoices';

interface CreateInvoiceParams {
  currency_type: 'crypto' | 'fiat';
  asset?: string;
  fiat?: string;
  accepted_assets?: string;
  amount: string;
  description?: string;
  hidden_message?: string;
  paid_btn_name?: 'viewItem' | 'openChannel' | 'openBot' | 'callback';
  paid_btn_url?: string;
  allow_comments?: boolean;
  allow_anonymous?: boolean;
  expires_in?: number;
}

interface Invoice {
  invoice_id: number;
  status: 'active' | 'paid' | 'expired';
  bot_invoice_url: string; 
  mini_app_invoice_url: string;
}

interface ApiResponse<T> {
  ok: boolean;
  result?: T;
  error?: {
    code: number;
    name: string;
  };
}

export const processPayment = async (cart: Product[], asset: CryptoAsset) => {
  const timestamp = Date.now();
  console.log(`💳 Initiating Direct Server Payment [${timestamp}]...`);

  const totalAmount = cart.reduce((sum, item) => sum + item.price, 0);
  const baseUrl = window.location.origin + window.location.pathname;
  // Clean URL params for the return link
  const returnUrl = `${baseUrl.split('?')[0]}?payment=verifying`;

  const params: CreateInvoiceParams = {
    currency_type: 'fiat',
    fiat: 'USD',
    amount: totalAmount.toFixed(2),
    accepted_assets: asset, 
    description: `OFM HUB: ${cart.length} Items`,
    hidden_message: "Access Granted. Welcome to the Hub.",
    paid_btn_name: 'callback',
    paid_btn_url: returnUrl,
    allow_comments: false,
    allow_anonymous: false,
    expires_in: 3600
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s Timeout

    const response = await fetch(CREATE_INVOICE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Crypto-Pay-API-Token': CRYPTO_BOT_TOKEN
      },
      body: JSON.stringify(params),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Gateway Error: ${response.status}`);
    }

    const data: ApiResponse<Invoice> = await response.json();

    if (!data.ok || !data.result) {
       console.error(`❌ CryptoBot API Error:`, data);
       return { success: false, message: `API Error: ${data.error?.name || 'Unknown'}` };
    }

    console.log(`✅ Invoice Created: ${data.result.invoice_id}`);
    
    // Log order to DB immediately with pending status
    await DB.createOrder({
      invoiceId: data.result.invoice_id,
      status: 'pending',
      totalAmount: totalAmount,
      currency: asset,
      items: cart.map(p => ({ id: p.id, title: p.title, price: p.price }))
    });

    return {
      success: true,
      invoiceUrl: data.result.bot_invoice_url,
      invoiceId: data.result.invoice_id
    };

  } catch (error: any) {
    console.error("❌ Payment Flow Error:", error);
    return { 
      success: false, 
      message: error.message || "Connection Failed" 
    };
  }
};

// --- CRITICAL: VERIFICATION LOGIC ---
export const checkInvoiceStatus = async (invoiceId: number): Promise<'paid' | 'active' | 'expired' | 'error'> => {
  try {
    // We query CryptoBot directly to see if the status changed
    const response = await fetch(`${GET_INVOICES_ENDPOINT}?invoice_ids=${invoiceId}`, {
        method: 'GET',
        headers: {
            'Crypto-Pay-API-Token': CRYPTO_BOT_TOKEN
        }
    });

    if (!response.ok) return 'error';

    const data: ApiResponse<{ items: Invoice[] }> = await response.json();
    
    if (data.ok && data.result && data.result.items.length > 0) {
        const invoice = data.result.items[0];
        console.log(`🔍 Invoice ${invoiceId} Status: ${invoice.status.toUpperCase()}`);
        return invoice.status;
    }
    
    return 'error';
  } catch (e) {
    console.error("Verification failed", e);
    return 'error';
  }
};